// https://material-ui.com/ru/api/select/
// https://material-ui.com/ru/api/menu-item/

import React from 'react';
import PropTypes from 'prop-types';
import uuidv4 from 'uuid/v4';

import { compose } from 'redux';
import { withStyles } from '@material-ui/styles';
import withHelperText from "../../hocs/with_helper_text.js";
import withGuid from "../../hocs/with_guid.js";

import { MenuItem, 
         TextField } from '@material-ui/core';
import SelectField, { selectStyles } from "./select_field.js";

import I18n from "../../base/i18n.js";

import IcCheckMark from '../../resources/icons/ic_check_mark.js.jsx';

import { InputVariantStandard, InputVariantOutlined, InputVariantFilled,
         InputLabelDisplayModeInside, InputLabelDisplayModeAbove,
         ClearItemValue } from "./constants.js.jsx";

const styles = theme => ({
  textFieldContainer: {
    padding: "0px 16px",
    marginBottom: "10px"
  }
});

/**
 * Callback for inputs
 * @callback ModelField~onChange
 * @param {string} value input's new value
 */

/**
 * Creates label for downloaded from server data
 * @function ModelField~displayNameFunc
 * @param {object} option server item
 * @returns {string} display value for dropdown menu
 */

/**
 * Component for select value from remote data source (API). Already includes HOCs {@link WithGuid}, {@link WithHelperText}
 * @name ModelField
 * @extends SelectField
 * @param {object} props React component properties
 * @param {object} [props.value] Field's value
 * @param {BaseUseCase} props.useCase Use case for downloading data from server
 * @param {number} [props.sendQueryTimeout = 500] Timeout for typing and send request to server on typing query
 * @param {string} [props.primaryKey = "id"] Name of field from props.value to send it to server
 * @param {Object.<string: any>} [props.queryParams = {perPage: 50}] Fixed params for server request
 * @param {ModelField~displayNameFunc} [props.displayNameFunc = (option) => { return option.name }] Creates display name for item from server in dropdown menu
 * @param {ModelField~onChange} [props.onChange] On change handler
 * @example
 * return (
 *   <ModelField className={ "awesome-class" } 
 *               onChange = { (value) => {
 *                 // Some actions
 *               } } 
 *               useCase = { LoadCitiesUseCase }
 *               value = { {id: 1, name: "Novosibirsk"} } />
 * )
*/
class ModelField extends SelectField {
  constructor(props) {
    super(props);
    this.searchKey = uuidv4();
    this.isFirstOpen = true;

    this.state = {
      options: props.value ? [props.value] : [],
      query: "",
    }

    this.queryParams = {
      ...props.queryParams
    }

    this.onOpen = this.onOpen.bind(this);
    this.onQueryChange = this.onQueryChange.bind(this);
    this.loadData = this.loadData.bind(this);

    this.processValue = this.processValue.bind(this);
    this.isSelected = this.isSelected.bind(this);
  }

  componentDidMount() {
    const { multiple, useCase } = this.props;
    let { valueId } = this.props;
    const { value } = this.state;

    if(!valueId && multiple) {
      valueId = [];
    }
    if(!multiple && !Array.isArray(valueId) && valueId) {
      valueId = [valueId];
    }


    if(valueId && (!value || value.length === 0)) {
      let requestParams = {
        ids: valueId
      }

      this.setState({
        loading: true
      })
      useCase.execute(requestParams)
      .then((result) => {
        this.setState({
          loading: false
        })
        const { data: { list } } = result;
        const value = multiple ? [...list] : list[0];
        this.setState({
          loading: false,
          value: value,
          options: this.updateOptions(list, value)
        })
      })
      .catch((result) => {
        this.setState({
          loading: false
        })
      })
    }
  }

  static getDerivedStateFromProps(props, state) {
    let value;

    const { multiple } = props;

    let emptyValue = multiple ? [] : undefined;

    let propsValue   = props.value || emptyValue;
    let defaultValue = state.defaultValue || emptyValue;
    let stateValue   = state.value || emptyValue;
    
    if(JSON.stringify(defaultValue) !== JSON.stringify(propsValue)) {
      defaultValue = propsValue;
      value = propsValue;
    }
    else {
      value = stateValue;
    }

    return {
      ...state,
      value: value,
      defaultValue: defaultValue
    }
  }

  shouldComponentUpdate(nextProps, nextState) {
    return super.shouldComponentUpdate(nextState, nextState) ||
           JSON.stringify(this.state.value) !== JSON.stringify(nextState.value) ||
           JSON.stringify(this.state.options) !== JSON.stringify(nextState.options) ||
           this.state.query !== nextState.query;
  }

  onOpen() {
    this.isFirstOpen = false;
    this.loadData();
  }

  onQueryChange(event) {
    let value = event.target.value;
    this.queryParams.query = value;
    this.setState({
      query: value
    })

    if(this.timeoutToken) {
      clearTimeout(this.timeoutToken);
    }
    this.timeoutToken = setTimeout(() => {
      clearTimeout(this.timeoutToken);
      this.timeoutToken = undefined;

      this.loadData();
    }, this.props.sendQueryTimeout)
  }

  onChange(event) {
    const { onChange } = this.props;
    const { options } = this.state;
    let valueId = event.target.value;
    
    let value = this.processValue(valueId);

    this.setState({
      value: value,
      options: this.updateOptions(options, value)
    });

    if(onChange) {
      onChange(value);
    }
  }

  processValue(newValue) {
    const { multiple, primaryKey } = this.props;
    const { options } = this.state;

    if(!multiple && newValue === ClearItemValue) {
      return "";
    }
    if(multiple && newValue.includes(ClearItemValue)) {
      return [];
    }

    if(Array.isArray(newValue)) {
      newValue = newValue.map(val => `${val}`);
    }
    else {
      newValue = `${newValue}`;
    }

    let values = options.filter((item) => {
      let itemId = `${item[primaryKey]}`;
      if(!multiple) {
        return itemId === newValue
      }
      return newValue.includes(itemId);
    })

    this.queryParams.orIds = values.map(val => `${val[primaryKey]}`);

    return multiple ? values : values[0];
  }

  renderValue() {
    const { multiple, clearItem } = this.props;
    const { value } = this.state;
    
    if(!multiple && !value) {
      return clearItem;
    }
    if(multiple && value.length === 0) {
      return clearItem;
    }
    if(!multiple) {
      return this.displayNameFunc(value);
    }

    return value.map((val) => {
      return this.displayNameFunc(val);
    }).join(", ")
  }

  isSelected(val) {
    const { multiple, primaryKey } = this.props;
    const valId = `${val[primaryKey]}`;
    if(!multiple) {
      const { value = {} } = this.state;
      const valueId = `${value[primaryKey]}`;
      return valId === valueId;
    }
    
    const { value } = this.state;
    let valueIds = value.map(val => `${val[primaryKey]}`)
    return valueIds.includes(valId);
  }

  loadData() {
    const { useCase } = this.props;
    const { value } = this.state;
    this.setState({
      loading: true
    })
    
    useCase.execute(this.queryParams)
    .then((result) => {
      this.setState({
        loading: false
      })
      const { data: { list } } = result;
      this.setState({
        loading: false,
        options: this.updateOptions(list, value)
      })
    })
    .catch((result) => {
      this.setState({
        loading: false
      })
    })
  }

  displayNameFunc(option) {
    if(!option) {
      return "";
    }
    return this.props.displayNameFunc(option);
  }

  updateOptions(options, value) {
    const { primaryKey } = this.props;
    let newOptions = [...options];
    if(value) {
      let index = newOptions.findIndex(item => `${item[primaryKey]}` === `${value[primaryKey]}`)
      if(index >= 0) {
        newOptions.splice(index, 1);
      }
      newOptions.unshift({...value});
    }
    return newOptions;
  }

  value() {
    const { primaryKey, multiple } = this.props;
    const { value } = this.state;

    if(!value) {
      return "";
    }
    if(!multiple)
      return `${value[primaryKey]}`;
    return value.map(val => `${val[primaryKey]}`)
  }

  pickerContent() {
    const { classes, primaryKey, allowClear } = this.props;

    const { options, query } = this.state;

    let items = options.map((option) => {
      let val = option[primaryKey];
      return <MenuItem key = { val }
                       value = { val }
                       aria-disabled = { true }
                       className = { classes.menuItem } >
        <span className = { classes.text } >
          { this.displayNameFunc(option) }
        </span>
        {
          this.isSelected(option) &&
          <IcCheckMark className = { classes.checkMark } />
        }
      </MenuItem>
    })
    if(allowClear) {
      items.unshift(this.clearItem());
    }
    items.unshift(<div className = { classes.textFieldContainer }
                       key = { this.searchKey } >
                    <TextField variant = { "outlined" }
                               fullWidth
                               value = { query }
                               onChange = { this.onQueryChange } 
                               onClick = { (event) => {
                                 event.stopPropagation();
                                 event.preventDefault();
                               }} />
                  </div>)
    return items;
  }
}

ModelField.propTypes = {
  variant: PropTypes.oneOf([InputVariantStandard, InputVariantOutlined, InputVariantFilled]),
  displayNamePosition: PropTypes.oneOf([InputLabelDisplayModeAbove, InputLabelDisplayModeInside]),
  allowClear: PropTypes.bool,
  multiple: PropTypes.bool,
  useCase: PropTypes.object.isRequired,
  sendQueryTimeout: PropTypes.number,
  primaryKey: PropTypes.string,
  queryParams: PropTypes.object,
  displayNameFunc: PropTypes.func,
};

ModelField.defaultProps = {
  variant: InputVariantOutlined,
  displayNamePosition: InputLabelDisplayModeInside,
  underline: true,
  sendQueryTimeout: 500,
  primaryKey: "id",
  queryParams: {
    perPage: 50,
  },
  displayNameFunc: (option) => {
    return option.name;
  },
  allowClear: false,
  clearItem: I18n.t("shared.all"),
  multiple: false
}

export default compose(
  withGuid, withHelperText, 
  withStyles(
    (theme) => ({
      ...selectStyles(theme),
      ...styles(theme),
    }),
    { withTheme: true }
  )
)(ModelField)
